/** @file Composable for managing the Surface Add Post button functionality.
 * Provides the click handler, visibility logic, and animation/tooltip states.
 */
import appCan from '@@/bits/app_can'
import device from '@@/bits/device'
import { usePostComposerModalStore } from '@@/pinia/post_composer_modal_store'
import { useSurfaceStore } from '@@/pinia/surface'
import { useSurfaceCurrentUserStore } from '@@/pinia/surface_current_user'
import { useSurfaceDraftsStore } from '@@/pinia/surface_drafts'
import { useSurfaceMapStore } from '@@/pinia/surface_map'
import { useSurfaceOnboardingDemoPadletPanelCopyStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_copy_store'
import { useSurfaceOnboardingDemoPadletPanelStore } from '@@/pinia/surface_onboarding_demo_padlet_panel_store'
import { useSurfacePermissionsStore } from '@@/pinia/surface_permissions'
import { useSurfacePostsStore } from '@@/pinia/surface_posts'
import type { PostAttributes } from '@@/types'
import { useReducedMotionMode } from '@@/vuecomposables/useReducedMotionMode'
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

export function useSurfaceAddPostButton() {
  const surfaceStore = useSurfaceStore()
  const postComposerModalStore = usePostComposerModalStore()
  const surfacePermissionsStore = useSurfacePermissionsStore()
  const surfaceDraftsStore = useSurfaceDraftsStore()
  const surfacePostsStore = useSurfacePostsStore()
  const surfaceMapStore = useSurfaceMapStore()
  const surfaceDemoPadletPanelStore = useSurfaceOnboardingDemoPadletPanelStore()
  const surfaceDemoPadletPanelCopyStore = useSurfaceOnboardingDemoPadletPanelCopyStore()
  const surfaceCurrentUserStore = useSurfaceCurrentUserStore()

  const { isMap } = storeToRefs(surfaceStore)
  const { startPickingLocation } = surfaceMapStore
  const { startNewPost } = surfacePostsStore
  const { currentUser } = storeToRefs(surfaceCurrentUserStore)
  const { isInReducedMotionMode } = useReducedMotionMode(currentUser)
  const { hasActiveComposerModal } = storeToRefs(postComposerModalStore)

  /**
   * Determines if the add post button can be shown.
   * Checks various conditions including permissions, layout, and modal states.
   */
  const canShowAddPost = computed(() => {
    const isAppWithNewPosting = device.app && appCan('newPostModal')
    const isActiveDraftExpanded = postComposerModalStore.hasExpandedComposerModal && device.desktop && !device.phone
    const isAPostBeingEdited = !!surfacePostsStore.postBeingEditedCid
    return (
      !isAppWithNewPosting &&
      (surfaceStore.isEmbedded || !surfaceStore.isMobileLayout) &&
      surfacePermissionsStore.canIWrite &&
      !surfaceStore.isReadOnly &&
      !surfaceStore.isFrozen &&
      (!isAPostBeingEdited || !isActiveDraftExpanded) &&
      surfaceDraftsStore.hasFetchedInitialPostDrafts &&
      // Reason why we can only show the add post button after dockable modal list is mounted:
      // `DockableModalList` has a prop called `initialModalState`. In a normal case, it is set
      // to `Minimized`. This is so that after we fetch post drafts, they appear as minimized.
      // If we show the add post button before the dockable modal list is mounted, and the user
      // clicks on the button to create a new draft, this draft will be considered as a draft
      // we fetch from the API and will appear minimized. This is not the desired behavior.
      // So if we delay showing this button until dockable modal list is mounted, we can avoid
      // the issue as users can only create drafts after dockable modal list is already rendered.
      // However, it is not perfect because we sacrifice Time to Interactive for this.
      // TODO: Improve this fix
      postComposerModalStore.isDockableModalListMounted
    )
  })

  /**
   * Determines if the add post button should be visible.
   * Excludes submission request mode.
   */
  const xAddPostButton = computed(() => {
    return canShowAddPost.value && !surfaceStore.isSubmissionRequest
  })

  /**
   * Determines if the add post button should animate.
   * Disabled when in reduced motion mode or when a composer modal is active.
   */
  const shouldAnimateAddPostButton = computed(() => !isInReducedMotionMode.value && !hasActiveComposerModal.value)

  /**
   * Determines if the add post button should show its tooltip.
   * Only shown for non-map walls when there are no drafts.
   */
  const shouldShowAddPostButtonTooltip = computed(() => !isMap.value && surfaceDraftsStore.draftCids.length === 0)

  /**
   * Handles the click event on the add post button.
   * Opens map location picker for map walls, or starts a new post otherwise.
   */
  const handleAddPostButtonClick = (): void => {
    surfaceStore.setAddPostButtonClickStatus(true)
    if (isMap.value) {
      startPickingLocation()
    } else {
      if (surfaceDemoPadletPanelStore.isDemoPadletPanelDesktop && surfaceDemoPadletPanelStore.isPostStep) {
        surfaceDemoPadletPanelStore.setFavoritePlacesSectionAsMostRecentlyTouched()
        // Limit to one post draft at a time
        if (!surfaceDraftsStore.isAnyDraftActive) {
          const attributes: PostAttributes = {
            ...surfaceDemoPadletPanelCopyStore.postData[0],
          }
          startNewPost({ attributes })
        }
      } else {
        startNewPost({})
      }
    }
  }

  return {
    xAddPostButton,
    shouldAnimateAddPostButton,
    shouldShowAddPostButtonTooltip,
    handleAddPostButtonClick,
  }
}
